/**
 * useOrchestration — cross-context orchestration layer.
 *
 * Wires together canvas, chat, patient panel, and observer systems.
 * Ported from JSX codebase (master:frontend/src/hooks/useOrchestration.js)
 * to TypeScript, adapted for the Python/FastAPI backend SSE protocol.
 *
 * `sendToAgent` returns `{ output, tools } | null` directly so callers
 * get the response without reading stale React state.
 */

import { useCallback, useState, useRef } from 'react'
import type { CanvasTab, UseCanvasReturn } from './useCanvas'
import type { UsePatientPanelReturn } from './usePatientPanel'
import { stripRelay, parseMedReconJSON } from './usePatientPanel'
import { renderIPASSHtml, parseIPASS, parseLOC, mergeIPASS } from '../utils/ipassParser'
import type { AgentToolCall } from '../contexts/ChatContext'
import { AGENT_LABELS } from '../utils/agentUtils'
import { stripHtml } from '../utils/domUtils'
import { TAB_LABELS, normalizeTabKey } from '../utils/canvasUtils'

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

// AGENT_LABELS imported from ../utils/agentUtils

/** Group event emitted by orchestration actions */
export interface GroupEvent {
  type: string
  agentId: string
  targetAgentId?: string
  icon?: string
  title: string
  detail?: string
}

/** Agent response extracted from the latest assistant message */
interface AgentResponse {
  output: string
  tools: AgentToolCall[]
}

/** Observer hook interface (minimal — see useObserver.ts) */
interface ObserverHandle {
  recordEvent: (event: ObservableEvent) => void
}

export interface ObservableEvent {
  type: string
  source: string
  agentId: string
  title: string
  detail?: string
}

/** Dependencies injected into useOrchestration */
interface OrchestrationDeps {
  workspaceId: string | null
  canvas: UseCanvasReturn
  panel: UsePatientPanelReturn
  sendToAgent: (agentId: string, message: string, mode?: string, canvasSnapshots?: Record<string, string>, options?: { hidden?: boolean; orchestrate?: boolean }) => Promise<AgentResponse | null>
  addGroupEvent?: (event: GroupEvent) => void
  observer?: ObserverHandle
  pipelineRunningRef?: React.MutableRefObject<boolean>
  setCanvasOpen?: (open: boolean) => void
}

/** Return type of useOrchestration */
export interface UseOrchestrationReturn {
  isSyncing: boolean
  handleSend: (agentId: string, text: string, files?: File[], mode?: string) => Promise<void>
  handleEditAndResend: (agentId: string, messageId: string, newText: string, mode?: string) => Promise<void>
  handleSaveDraft: (tab: CanvasTab) => Promise<void>
  handleRebuild: (tab: CanvasTab) => Promise<void>
  handleReformat: (tab: CanvasTab) => Promise<void>
  handleSelectionPrompt: (selectedText: string, instruction: string) => Promise<void>
  handleNewFilesUploaded: (files: File[], metadata: Array<{ name: string }>) => Promise<void>
  refreshPatientPanel: () => Promise<void>
  optimizeBillingHeaders: () => Promise<void>
  tryUpdateIPASS: (output: string) => void
}

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

// TAB_LABELS and normalizeTabKey imported from ../utils/canvasUtils

/** Determine which agent owns a given canvas tab */
const tabOwnerAgent = (tab: CanvasTab): string =>
  tab === 'assessment_and_plan' ? 'clinical-reasoning' : 'hpi-pe-agent'

// stripHtml imported from ../utils/domUtils

/** Extract active-problem compound headers from A&P text */
const extractProblemHeaders = (apText: string): string[] => {
  if (!apText) return []
  const headers: string[] = []
  const re = /^#{2,3}\s+(.+?,\s*POA)\s*$/gim
  let m: RegExpExecArray | null
  while ((m = re.exec(apText)) !== null) {
    headers.push(m[1].trim())
  }
  return headers
}

/** Build synthetic med-recon markdown from structured data */
const buildMedReconMarkdown = (medData: object): string => {
  const lines = ['## MEDICATION RECONCILIATION']
  for (const [key, items] of Object.entries(medData)) {
    if (!Array.isArray(items)) continue
    const title = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    lines.push(`### ${title}`)
    for (const item of items) {
      lines.push(`- ${String(item)}`)
    }
  }
  return lines.join('\n')
}

// ────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────

export function useOrchestration(deps: OrchestrationDeps): UseOrchestrationReturn {
  const {
    workspaceId,
    canvas,
    panel,
    sendToAgent,
    addGroupEvent: emitGroupEvent,
    observer,
    pipelineRunningRef,
    setCanvasOpen,
  } = deps

  const [isSyncing, setIsSyncing] = useState(false)

  // Stable ref for canvas tab content access in closures
  const canvasRef = useRef(canvas)
  canvasRef.current = canvas

  // Single enforcement point: every background/pipeline turn in this hook routes
  // through this wrapper, so orchestrate:false is structural — a stray relay
  // marker must never grow a visible consult pill on system-composed traffic, and
  // a future call site gets the safe behavior without remembering the option tail.
  // Only the physician-typed paths (handleSend, handleEditAndResend) call
  // sendToAgent directly and leave orchestration on.
  const sendBackground = useCallback((
    agentId: string,
    message: string,
    mode?: string,
    canvasSnapshots?: Record<string, string>
  ) => sendToAgent(agentId, message, mode, canvasSnapshots, { orchestrate: false }),
  [sendToAgent])

  // ── noop helpers for optional deps ──
  const addGroupEvent = useCallback((event: GroupEvent) => {
    emitGroupEvent?.(event)
  }, [emitGroupEvent])

  const recordObserverEvent = useCallback((event: ObservableEvent) => {
    observer?.recordEvent(event)
  }, [observer])

  // ── tryUpdateIPASS ──
  const tryUpdateIPASS = useCallback((output: string) => {
    if (!output) return
    const sections = parseIPASS(output)
    const loc = parseLOC(output)
    if (Object.keys(sections).length === 0 && !loc) return

    const existingKey = `casey-ipass-sections-${workspaceId || 'default'}`
    let existing: ReturnType<typeof parseIPASS> = {}
    let existingLoc: ReturnType<typeof parseLOC> = null
    try {
      const raw = sessionStorage.getItem(existingKey)
      if (raw) {
        const parsed = JSON.parse(raw)
        existing = parsed.sections || {}
        existingLoc = parsed.loc || null
      }
    } catch (err) { console.warn('[useOrchestration] Failed to restore IPASS sections from sessionStorage:', err) }

    const merged = mergeIPASS(existing, sections)
    const finalLoc = loc || existingLoc
    const html = renderIPASSHtml(merged, finalLoc)
    panel.updateIpassHtml(html)

    try {
      sessionStorage.setItem(existingKey, JSON.stringify({ sections: merged, loc: finalLoc }))
    } catch (err) { console.warn('[useOrchestration] Failed to persist IPASS sections to sessionStorage:', err) }
  }, [workspaceId, panel])

  // ── refreshPatientPanel ──
  const refreshPatientPanel = useCallback(async () => {
    const snapshots = canvasRef.current.getCanvasSnapshots()
    const hpiText = snapshots.hpi
    const peText = snapshots.pe
    const apText = snapshots.assessment_and_plan

    if (!hpiText && !peText && !apText) return

    panel.setIsLoading(true)
    try {
      const refreshMsg =
        `[CANVAS_UPDATE] The clinical note canvases have been updated. Here is the current content:\n\n` +
        `HPI:\n${hpiText || '(empty)'}\n\n` +
        `Physical Exam:\n${peText || '(empty)'}\n\n` +
        `Assessment & Plan:\n${apText || '(empty)'}\n\n` +
        `Re-generate your ## PATIENT SUMMARY, ## ADMIT ORDERS, and ## MEDICATION RECONCILIATION based on the updated content above. ` +
        `IMPORTANT: Align medication decisions in the Medication Reconciliation with the A&P plan -- if the A&P adds, adjusts, holds, or discontinues medications, reflect those changes.`

      const result = await sendBackground('casey', refreshMsg)

      if (result?.output) {
        const panelText = result.output
        const parsed = panel.processResponse(stripRelay(panelText) || '')

        if (parsed.medications) panel.setMedsStatus('reconciled')

        // Check for med-recon in tool outputs
        if (!parsed.medications) {
          const medReconTool = result.tools.find(
            t => t.name === 'reconcile_meds' || t.name === 'reconcile_medications'
          )
          if (medReconTool?.output) {
            const medData = parseMedReconJSON(medReconTool.output)
            if (medData) {
              panel.processResponse(buildMedReconMarkdown(medData))
            }
          }
        }
      }
    } catch (err) {
      console.error('Panel refresh error:', err)
    } finally {
      panel.setIsLoading(false)
    }
  }, [sendBackground, panel])

  // ── optimizeBillingHeaders ──
  const optimizeBillingHeaders = useCallback(async () => {
    const apTab = canvasRef.current.tabs.assessment_and_plan
    const apText = stripHtml(apTab.html)
    const headers = extractProblemHeaders(apText)

    if (headers.length === 0) return

    const billingMsg =
      `[BILLING_HEADER_REVIEW] Review and optimize these A&P problem headers for billing. ` +
      `Each header must be specific enough to map directly to an ICD-10 code. ` +
      `Include laterality, acuity, etiology, and anatomic site when supported by the chart data. ` +
      `Preserve POA suffixes and " | " pipe separators exactly. ` +
      `Return ONLY a JSON array of replacements. Only include headers that need improvement. ` +
      `Format: [{"original": "exact original header", "optimized": "billing-optimized header"}]\n\n` +
      `HEADERS:\n${headers.map((h, i) => `${i + 1}. ${h}`).join('\n')}\n\n` +
      `If ALL headers are already billing-optimal, return an empty array: []`

    try {
      const result = await sendBackground('casey', billingMsg)

      if (!result?.output) return

      const jsonMatch = result.output.match(/\[[\s\S]*?\]/)
      if (!jsonMatch) return

      let replacements: Array<{ original: string; optimized: string }>
      try {
        replacements = JSON.parse(jsonMatch[0])
      } catch (err) {
        console.error('[useOrchestration] Failed to parse billing header replacements JSON:', err)
        return
      }

      if (!Array.isArray(replacements) || replacements.length === 0) return

      let currentContent = canvasRef.current.tabs.assessment_and_plan.content
      if (!currentContent) return

      let applied = 0
      for (const { original, optimized } of replacements) {
        if (!original || !optimized || original === optimized) continue
        if (currentContent.includes(original)) {
          currentContent = currentContent.replace(original, optimized)
          applied++
        }
      }

      if (applied > 0) {
        canvasRef.current.setTabContent('assessment_and_plan', currentContent, 'agent')
        addGroupEvent({
          type: 'agent_status',
          agentId: 'casey',
          title: `Casey optimized ${applied} billing header${applied > 1 ? 's' : ''} for ICD-10 specificity`,
        })
      }
    } catch (err) {
      console.error('[BillingHeaders] Error:', err)
    }
  }, [sendBackground, addGroupEvent])

  // ── handleSend ──
  const handleSend = useCallback(async (
    agentId: string,
    text: string,
    _files?: File[],
    mode?: string
  ) => {
    // Individual agent tab: standard handling
    const snapshots = canvasRef.current.getCanvasSnapshots()
    const result = await sendToAgent(agentId, text, mode, snapshots)

    if (result?.output || (result?.tools && result.tools.length > 0)) {
      if (agentId === 'casey') {
        panel.processResponse(result.output)
        tryUpdateIPASS(result.output)
      }

      if (['casey', 'clinical-reasoning', 'hpi-pe-agent'].includes(agentId)) {
        setCanvasOpen?.(true)

        recordObserverEvent({
          type: 'canvas_update',
          source: 'agent-generated',
          agentId,
          title: `${AGENT_LABELS[agentId] || agentId} updated canvas`,
          detail: result.tools?.map(t => t.name).filter(Boolean).join(', ') || undefined,
        })

        if (!pipelineRunningRef?.current) {
          panel.scheduleRefresh(() => refreshPatientPanel())
        }
      }

      // Record significant tool completions
      if (result.tools?.length > 0) {
        const significantTools = new Set([
          'generate_hpi_pe', 'generate_ap', 'filter_evidence',
          'reconcile_meds', 'review_hpi_pe', 'review_ap',
          'search_clinical_evidence', 'save_chart_data',
        ])
        for (const tool of result.tools) {
          if (significantTools.has(tool.name)) {
            recordObserverEvent({
              type: 'tool_completion',
              source: 'agent-generated',
              agentId,
              title: `${AGENT_LABELS[agentId] || agentId} completed ${tool.name}`,
              detail: `output length: ${tool.output?.length || 0}`,
            })
          }
        }
      }
    }
  }, [sendToAgent, panel, tryUpdateIPASS, refreshPatientPanel, recordObserverEvent, setCanvasOpen, pipelineRunningRef])

  // ── handleEditAndResend ──
  const handleEditAndResend = useCallback(async (
    agentId: string,
    _messageId: string,
    newText: string,
    mode?: string
  ) => {
    const snapshots = canvasRef.current.getCanvasSnapshots()
    const result = await sendToAgent(agentId, newText, mode, snapshots)

    if (result?.output || (result?.tools && result.tools.length > 0)) {
      if (agentId === 'casey') {
        panel.processResponse(result.output)
        tryUpdateIPASS(result.output)
      }

      if (['casey', 'clinical-reasoning', 'hpi-pe-agent'].includes(agentId)) {
        setCanvasOpen?.(true)

        if (!pipelineRunningRef?.current) {
          panel.scheduleRefresh(() => refreshPatientPanel())
        }
      }
    }
  }, [sendToAgent, panel, tryUpdateIPASS, refreshPatientPanel, setCanvasOpen, pipelineRunningRef])

  // ── handleSaveDraft ──
  const handleSaveDraft = useCallback(async (tab: CanvasTab) => {
    const content = canvasRef.current.tabs[tab].content
    if (!content) return

    const tabLabel = TAB_LABELS[tab] || tab

    // Notes tab: silent Casey review for clinical significance
    if (tab === 'notes') {
      const plainText = stripHtml(canvasRef.current.tabs.notes.html)
      if (!plainText.trim()) return

      setIsSyncing(true)
      try {
        const caseyResult = await sendBackground('casey',
          `[NOTES_REVIEW] The physician saved the following notes. Review for anything clinically significant ` +
          `that might affect patient care, documentation, or the current plan. If you find something actionable, ` +
          `include a <!-- ROUTE: tab --> marker with a clinical instruction for the owning agent. ` +
          `If nothing warrants changes, respond with exactly [NO_ACTION].\n\n` +
          `--- PHYSICIAN NOTES ---\n${plainText}\n--- END NOTES ---`
        )
        const response = caseyResult?.output || ''

        if (response && !response.includes('[NO_ACTION]')) {
          // Parse route markers
          const routePattern = /<!--\s*ROUTE:\s*(\w+)\s*-->([\s\S]*?)<!--\s*\/ROUTE\s*-->/gi
          const routes: Array<{ tab: string; instruction: string }> = []
          let match: RegExpExecArray | null
          while ((match = routePattern.exec(response)) !== null) {
            routes.push({ tab: match[1].toLowerCase(), instruction: match[2].trim() })
          }

          for (const route of routes) {
            if (!['ap', 'hpi', 'pe'].includes(route.tab)) continue
            const canvasTab = normalizeTabKey(route.tab) ?? 'hpi'
            const ownerAgentId = tabOwnerAgent(canvasTab)
            const currentPlain = stripHtml(canvasRef.current.tabs[canvasTab].html)
            const routeTabLabel = TAB_LABELS[canvasTab] || route.tab

            const agentResult = await sendBackground(ownerAgentId,
              `[NOTES_RELAY] Tab: ${routeTabLabel}\n\n` +
              `Casey reviewed the physician's notes and identified a specific clinical change needed.\n` +
              `INSTRUCTION: ${route.instruction}\n\n` +
              `--- CURRENT ${routeTabLabel.toUpperCase()} ---\n${currentPlain}\n--- END ---`
            )
            const agentResponse = agentResult?.output || ''

            if (agentResponse && !agentResponse.includes('[NO_CHANGES]')) {
              canvasRef.current.proposeSuggestion(canvasTab, agentResponse)
              canvasRef.current.setActiveTab(canvasTab)
              addGroupEvent({
                type: 'agent_reaction',
                agentId: ownerAgentId,
                title: `${AGENT_LABELS[ownerAgentId]} proposed ${routeTabLabel} update from physician notes`,
              })
            }
          }

          if (routes.length === 0) {
            addGroupEvent({
              type: 'agent_reaction',
              agentId: 'casey',
              title: 'Casey flagged findings from physician notes',
            })
          }
        }
      } catch (err) {
        console.error('[SaveNotes] Casey review failed:', err)
      } finally {
        setIsSyncing(false)
      }
      return
    }

    // Standard agent-owned tabs (HPI, PE, A&P)
    const ownerAgentId = tabOwnerAgent(tab)
    const ownerLabel = AGENT_LABELS[ownerAgentId] || ownerAgentId

    addGroupEvent({
      type: 'canvas_edit',
      agentId: ownerAgentId,
      title: `${ownerLabel} reviewing physician edits to ${tabLabel}`,
    })

    recordObserverEvent({
      type: 'user_edit',
      source: 'user-initiated',
      agentId: 'user',
      title: `Physician edited ${tabLabel} canvas`,
    })

    setIsSyncing(true)
    try {
      const plainText = stripHtml(canvasRef.current.tabs[tab].html)
      const result = await sendBackground(ownerAgentId,
        `[CANVAS_EDIT] Tab: ${tabLabel}\n\n` +
        `The physician has edited the ${tabLabel} canvas. Review the changes for clinical accuracy ` +
        `and coherence. If changes are needed, return the COMPLETE updated ${tabLabel}. ` +
        `If the edits look good, respond with [NO_CHANGES].\n\n` +
        `--- CURRENT ${tabLabel.toUpperCase()} ---\n${plainText}\n--- END ---`
      )
      const agentResponse = result?.output || ''

      if (!agentResponse) return

      if (agentResponse.includes('[NO_CHANGES]')) {
        addGroupEvent({
          type: 'agent_reaction',
          agentId: ownerAgentId,
          title: `${ownerLabel} approved edits to ${tabLabel} -- no changes needed`,
        })
        return
      }

      canvasRef.current.proposeSuggestion(tab, agentResponse)
      addGroupEvent({
        type: 'agent_reaction',
        agentId: ownerAgentId,
        title: `${ownerLabel} suggested changes to ${tabLabel}`,
      })
    } catch (err) {
      console.error('[SaveDraft] Error:', err)
    } finally {
      setIsSyncing(false)
      if (!pipelineRunningRef?.current) {
        panel.scheduleRefresh(() => refreshPatientPanel())
      }
    }
  }, [sendBackground, panel, addGroupEvent, recordObserverEvent, refreshPatientPanel, pipelineRunningRef])

  // ── handleRebuild ──
  const handleRebuild = useCallback(async (tab: CanvasTab) => {
    const ownerAgentId = tabOwnerAgent(tab)
    const ownerLabel = AGENT_LABELS[ownerAgentId] || ownerAgentId
    const tabLabel = TAB_LABELS[tab] || tab

    addGroupEvent({
      type: 'canvas_edit',
      agentId: ownerAgentId,
      title: `Rebuilding ${tabLabel} from scratch...`,
    })

    setIsSyncing(true)
    try {
      const currentDraft = stripHtml(canvasRef.current.tabs[tab].html)
      const notesPlain = stripHtml(canvasRef.current.tabs.notes.html)

      let rebuildMsg =
        `[REBUILD] Tab: ${tabLabel}\n\n` +
        `The physician wants you to regenerate the ${tabLabel} using all available chart data, ` +
        `clinical evidence, and physician input. `

      if (currentDraft) {
        rebuildMsg += `\n\n--- CURRENT ${tabLabel.toUpperCase()} DRAFT ---\n${currentDraft}\n--- END CURRENT DRAFT ---`
      }
      if (notesPlain) {
        rebuildMsg += `\n\n--- PHYSICIAN NOTES ---\n${notesPlain}\n--- END PHYSICIAN NOTES ---`
      }
      rebuildMsg += `\n\nPreserve the physician's edits and incorporate their notes. Return the COMPLETE regenerated ${tabLabel}.`

      const result = await sendBackground(ownerAgentId, rebuildMsg)
      const response = result?.output || ''

      if (response && !response.includes('[NO_CHANGES]')) {
        canvasRef.current.proposeSuggestion(tab, response)
        addGroupEvent({
          type: 'agent_reaction',
          agentId: ownerAgentId,
          title: `${ownerLabel} rebuilt ${tabLabel}`,
        })
      }
    } catch (err) {
      console.error('[Rebuild] Error:', err)
    } finally {
      setIsSyncing(false)
    }
  }, [sendBackground, addGroupEvent])

  // ── handleReformat ──
  const handleReformat = useCallback(async (tab: CanvasTab) => {
    const content = canvasRef.current.tabs[tab].content
    if (!content) return

    const ownerAgentId = tabOwnerAgent(tab)
    const ownerLabel = AGENT_LABELS[ownerAgentId] || ownerAgentId
    const tabLabel = TAB_LABELS[tab] || tab
    const plainText = stripHtml(canvasRef.current.tabs[tab].html)

    addGroupEvent({
      type: 'canvas_edit',
      agentId: ownerAgentId,
      title: `Formatting ${tabLabel}...`,
    })

    setIsSyncing(true)
    try {
      const result = await sendBackground(ownerAgentId,
        `[REFORMAT] Tab: ${tabLabel}\n\n` +
        `The physician wants you to clean up the formatting and style of the ${tabLabel} WITHOUT changing any clinical content. ` +
        `Fix heading hierarchy, bullet alignment, spacing, remove duplicate headers, ensure consistent style. ` +
        `Return the COMPLETE reformatted ${tabLabel} with identical clinical content.\n\n` +
        `--- CURRENT ${tabLabel.toUpperCase()} ---\n${plainText}\n--- END ---`
      )
      const response = result?.output || ''

      if (response && !response.includes('[NO_CHANGES]')) {
        canvasRef.current.proposeSuggestion(tab, response)
        addGroupEvent({
          type: 'agent_reaction',
          agentId: ownerAgentId,
          title: `${ownerLabel} reformatted ${tabLabel}`,
        })
      }
    } catch (err) {
      console.error('[Reformat] Error:', err)
    } finally {
      setIsSyncing(false)
    }
  }, [sendBackground, addGroupEvent])

  // ── handleSelectionPrompt ──
  const handleSelectionPrompt = useCallback(async (selectedText: string, instruction: string) => {
    if (!selectedText || !instruction) return

    const tab = canvasRef.current.activeTab
    const ownerAgentId = tabOwnerAgent(tab)
    const ownerLabel = AGENT_LABELS[ownerAgentId] || ownerAgentId
    const tabLabel = TAB_LABELS[tab] || tab
    const currentPlain = stripHtml(canvasRef.current.tabs[tab].html)

    addGroupEvent({
      type: 'canvas_edit',
      agentId: ownerAgentId,
      title: `Physician asked ${ownerLabel} to edit selection in ${tabLabel}`,
    })

    setIsSyncing(true)
    try {
      const result = await sendBackground(ownerAgentId,
        `[SELECTION_EDIT] Tab: ${tabLabel}\n\n` +
        `The physician selected the following text in the ${tabLabel} and asked you to edit it.\n\n` +
        `SELECTED TEXT:\n"""\n${selectedText}\n"""\n\n` +
        `PHYSICIAN'S INSTRUCTION: ${instruction}\n\n` +
        `Return the COMPLETE updated ${tabLabel} with only the selected portion changed according to the instruction. ` +
        `Keep everything else exactly the same.\n\n` +
        `--- CURRENT ${tabLabel.toUpperCase()} ---\n${currentPlain}\n--- END ---`
      )
      const response = result?.output || ''

      if (response && !response.includes('[NO_CHANGES]')) {
        canvasRef.current.proposeSuggestion(tab, response)
        addGroupEvent({
          type: 'agent_reaction',
          agentId: ownerAgentId,
          title: `${ownerLabel} suggested selection edit in ${tabLabel}`,
        })
      }
    } catch (err) {
      console.error('[SelectionEdit] Error:', err)
    } finally {
      setIsSyncing(false)
    }
  }, [sendBackground, addGroupEvent])

  // ── handleNewFilesUploaded ──
  const handleNewFilesUploaded = useCallback(async (
    _files: File[],
    metadata: Array<{ name: string }>
  ) => {
    addGroupEvent({
      type: 'file_upload',
      agentId: 'system',
      title: `${metadata.length} new file${metadata.length > 1 ? 's' : ''} uploaded`,
      detail: metadata.map(f => f.name).join(', '),
    })

    recordObserverEvent({
      type: 'file_upload',
      source: 'user-initiated',
      agentId: 'system',
      title: `Physician uploaded ${metadata.length} file${metadata.length > 1 ? 's' : ''}`,
      detail: metadata.map(f => f.name).join(', '),
    })

    try {
      const result = await sendBackground('casey',
        `[FILE_UPLOAD] Additional chart data files have been uploaded. Please process these files ` +
        `and update the patient panel, HPI/PE, and A&P as appropriate. ` +
        `Files: ${metadata.map(f => f.name).join(', ')}`
      )

      if (result?.output) {
        panel.processResponse(result.output)

        panel.scheduleRefresh(() => refreshPatientPanel())

        addGroupEvent({
          type: 'agent_status',
          agentId: 'casey',
          title: 'Casey processed uploaded files',
        })
      }
    } catch (err) {
      console.error('[FileUpload] Reprocessing failed:', err)
      addGroupEvent({
        type: 'agent_status',
        agentId: 'system',
        title: 'File reprocessing failed',
        detail: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }, [sendBackground, panel, refreshPatientPanel, addGroupEvent, recordObserverEvent])

  return {
    isSyncing,
    handleSend,
    handleEditAndResend,
    handleSaveDraft,
    handleRebuild,
    handleReformat,
    handleSelectionPrompt,
    handleNewFilesUploaded,
    refreshPatientPanel,
    optimizeBillingHeaders,
    tryUpdateIPASS,
  }
}
